import moment from "moment";
import { compile } from "expression-eval";
import ServiceUtils from "./service-utils";
import Field from "./field";
import Entity from "./entity";
import DocumentValue from "./document-value";
import { getEmptyPeriod, getEntryParams, momentGetUnit, replaceAll } from "../utils";
import ISubstitution from "../interface/substitution";
import IRelationPathField from "../interface/relation-path-field";
import IPeriodValue from "../interface/period-value";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormulaNumber from "../interface/field-cfg-formula-number";
import IConfigFormulaDatetime from "../interface/field-cfg-formula-datetime";
import IConfigFormula from "../interface/field-cfg-formula";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import IDateComplexValue from "../interface/date-complex-value";
import Global from "../global";
import SubstitutionOption from "../enum/substitution-option";
import ServiceUtil from "../enum/service-util";
import FormulaType from "../enum/formula-type";
import FieldType from "../enum/field-type";
import DatePeriod from "../enum/date-period";
import DateComplexType from "../enum/date-complex-type";
import DateComplexConst from "../enum/date-complex-const";

class FormulaInstance {

    /* Public Methods */

    public processDocument(entity: IEntity, doc: IDocument): IDocument {
        const fields: IField[] = Entity.filterFieldsByType(entity, [FieldType.formula]);
        fields.forEach((field: IField) => {
            const config: IConfigFormula = field.uiConfig as IConfigFormula;
            switch(config.type) {
                case FormulaType.number:
                    doc[`${Global.FORMULA_PROP_PREFIX}${field.id}`] = this.calcNumber(doc, config);
                    break;
                case FormulaType.datetime:
                    doc[`${Global.FORMULA_PROP_PREFIX}${field.id}`] = this.calcDatetime(doc, config);
                    break;
                case FormulaType.renewal:
                    doc[`${Global.FORMULA_PROP_PREFIX}${field.id}`] = this.calcRenewal(doc, config);
                    break;
            }
        });
        return doc;
    }

    public getNumberSubstitutions(entityId: number): ISubstitution[] {
        return ServiceUtils.substitutions(entityId, [FieldType.number], 1).map((sub: ISubstitution) => {
            sub.name = `[${sub.name}]`;
            return sub;
        });
    }

    public getDatetimeSubstitutions(entityId: number): ISubstitution[] {
        const result: ISubstitution[] = [];
        const subs: IRelationPathField[] = Field.getRelationPathsFields(entityId, [FieldType.datetime], 1);
        subs.forEach((sub: IRelationPathField) => {
            result.push({
                id: sub.pathId.join(','),
                pathId: sub.pathId,
                name: sub.pathName,
                option: SubstitutionOption.None,
                type: sub.type
            });
        });
        return result;
    }

    /* Public Methods - Number Expression */

    public expressionFormat(fieldId: number): string {
        const field: IField | null = Field.get(fieldId);
        if (field && field.uiType === FieldType.formula) {
            const entity: IEntity | null = Entity.get(field.entityId);
            const config: IConfigFormula = field.uiConfig as IConfigFormula;
            if (entity && config.type === FormulaType.number) {
                const formula: IConfigFormulaNumber = config.formula as IConfigFormulaNumber;
                const subs: ISubstitution[] = this.getNumberSubstitutions(entity.id);
                return ServiceUtils.replaceEntries(formula.expression, ServiceUtil.Sub, (params: (string | number)[]) => {
                    const path: string = params[0].toString();
                    const sub: ISubstitution | null = subs.find((s: ISubstitution) => {
                        return `${s.pathId}` === path;
                    }) || null;
                    if (sub) {
                        return `@${sub.name}`;
                    }
                    return '0';
                });
            }
        }
        return '';
    }

    public expressionValid(entityId: number, expression: string): boolean {
        const parsed: string = this.expressionParse(entityId, expression);
        const compile: string = ServiceUtils.replaceEntries(parsed, ServiceUtil.Sub, () => 1);
        const allowedChars: string[] = ['+', '-', '*', '/', '(', ')', '.', '|'];
        for (let i = 0; i < compile.length; i++) {
            if (!allowedChars.includes(compile[i]) && isNaN(Number(compile[i]))) {
                return false;
            }
        }
        return this.eval(compile) !== null;
    }

    /* Private Public Methods */

    public expressionParse(entityId: number, expression: string): string {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const subs: ISubstitution[] = this.getNumberSubstitutions(entity.id);
            let result: string = `${expression}`;
            subs.forEach((sub: ISubstitution) => {
                result = replaceAll(result, `@${sub.name}`, `[${ServiceUtil.Sub}](${sub.pathId})`)
            });
            return result;
        }
        return expression;
    }

    public getDependenciesNumber(config: IConfigFormulaNumber): number[] {
        const result: number[] = [];
        ServiceUtils.getEntries(config.expression, ServiceUtil.Sub).forEach((entry: string) => {
            const pathId: string | number = getEntryParams(entry)[0];
            if (typeof pathId === 'string') {
                const split: number[] = pathId.split(',').map((id: string) => Number(id));
                result.push(split[0]);
            } else if (typeof pathId === 'number') {
                result.push(pathId);
            }
        });
        return result;
    }

    public getDependenciesDatetime(config: IConfigFormulaDatetime): number[] {
        const result: number[] = [];
        if (config.from.type === DateComplexType.PathValue) {
            result.push((config.from.value as number[])[0]);
        }
        if (config.to.type === DateComplexType.PathValue) {
            result.push((config.to.value as number[])[0]);
        }
        return result;
    }

    public getDependenciesRenewal(config: IConfigFormulaRenewal): number[] {
        const result: number[] = [];
        if (config.from.type === DateComplexType.PathValue) {
            result.push((config.from.value as number[])[0]);
        }
        if (config.periodicityPath) {
            result.push(config.periodicityPath[0]);
        }
        return result;
    }

    /* Private Methods */

    private calcNumber(doc: IDocument, config: IConfigFormula): number | null {
        const formula: IConfigFormulaNumber = config.formula as IConfigFormulaNumber;
        const compile: string = ServiceUtils.replaceEntries(formula.expression, ServiceUtil.Sub, (params: string[] | number[]) => {
            let pathId: number[] = [];
            if (typeof params[0] === 'string') {
                pathId = (params[0] as string).split(',').map((id: string) => Number(id));
            } else if (typeof params[0] === 'number') {
                pathId = params as number[];
            }
            const docValue: IDocumentValue = DocumentValue.getByPathSync(doc, pathId);
            const value: number | null = DocumentValue.getNumber(docValue);
            return (value !== null ? value : NaN).toString();
        });
        return this.eval(compile);
    }

    private calcDatetime(doc: IDocument, config: IConfigFormula): IPeriodValue | null {
        const formula: IConfigFormulaDatetime = config.formula as IConfigFormulaDatetime;
        let from: number | null = this.getDateValue(doc, formula.from);
        const to: number | null = this.getDateValue(doc, formula.to);
        if (from && to) {
            return this.getPeriodValue(from, to, config.format as DatePeriod[]);
        }
        return null;
    }

    private calcRenewal(doc: IDocument, config: IConfigFormula): string | IPeriodValue | null {
        const formula: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
        let from: number | null = this.getDateValue(doc, formula.from);
        let periodicity: number | null = formula.periodicity;
        if (formula.periodicityPath !== null) {
            const value: IDocumentValue = DocumentValue.getByPathSync(doc, formula.periodicityPath);
            periodicity = DocumentValue.getNumber(value);
        }
        if (from && typeof periodicity === 'number') {
            const now: number = this.getDateValue(doc, {
                type: DateComplexType.ConstantDate,
                value: DateComplexConst.Now
            }) as number;
            const period: IPeriodValue = this.getPeriodValue(from, now, []).full as IPeriodValue;
            const unit: moment.unitOfTime.Diff = momentGetUnit(formula.period);
            let diff: number = Math.floor(period[formula.period] / periodicity) + 1;
            if (formula.periodicityMax && diff > formula.periodicityMax) {
                diff = formula.periodicityMax;
            }
            const next: moment.Moment = moment(from);
            if (!period.overdue) {
                next.set(unit, next.get(unit) + Math.ceil(diff * periodicity));
            }
            if (formula.showPeriod) {
                return this.getPeriodValue(now, next.valueOf(), config.format as DatePeriod[]);
            } else {
                return next.toISOString();
            }
        }
        return null;
    }

    private getDateValue(doc: IDocument, date: IDateComplexValue): number | null {
        switch (date.type) {
            case DateComplexType.ConstantDate:
                return (new Date()).getTime();
            case DateComplexType.ExactDate:
                return (new Date(date.value as string)).getTime();
            case DateComplexType.PathValue:
                const pathId: number[] = date.value as number[];
                const value: IDocumentValue = DocumentValue.getByPathSync(doc, pathId);
                if (value) {
                    const d: Date = new Date(value as string);
                    if (!isNaN(d.getTime())) {
                        return d.getTime();
                    }
                }
        }
        return null;
    }

    private getPeriodValue(from: number, to: number, format: DatePeriod[]): IPeriodValue {
        const result: IPeriodValue = getEmptyPeriod();
        let date: moment.Moment;
        if (from > to) {
            const swapFrom: number = from;
            result.overdue = true;
            date = moment(from);
            from = to;
            to = swapFrom;
        }
        date = moment(to);
        const momentFrom: moment.Moment = moment(from);
        const momentTo: moment.Moment = moment(to);
        const full: IPeriodValue = getEmptyPeriod(result.overdue);
        const periods: DatePeriod[] = [DatePeriod.Year, DatePeriod.Month, DatePeriod.Day, DatePeriod.Hour, DatePeriod.Minute, DatePeriod.Second];
        for (let i = 0; i < periods.length; i++) {
            const period: DatePeriod = periods[i];
            const unit: moment.unitOfTime.Diff = momentGetUnit(period);
            full[period] = momentTo.diff(momentFrom, unit);
            if (format.includes(period)) {
                result[period] = date.diff(momentFrom, unit);
                date.set(unit, date.get(unit) - (result[period]));
            }
        }
        result.full = full;
        return result;
    }

    private eval(expression: string): number | null {
        try {
            const result: number = compile(expression)({});
            if (!isNaN(result) && isFinite(result)) {
                return result;
            }
        } catch (e) { }
        return null;
    }

}

const Formula = new FormulaInstance();

export default Formula;